// Web v3 copy, canonical comp updated October 2, 2026.
// Outcome claims remain gated by existing authoritative state.
// Spanish: the Founder's full review of 2026-10-06, applied by scripts/apply-spanish-review.js.

export const en = {
  "partner.title": "Partner onboarding",

  "partner.step.tax": "Tax documents",
  "partner.step.legal": "Legal agreements",
  "partner.step.payout": "Payout account",
  "partner.step.where.app": "in the Lura app",
  "partner.step.where.either": "here or in the app",
  "partner.step.done": "Done",
  "partner.step.todo": "Not finished",
  "partner.step.line": "{label}: {state}",

  "partner.loading": "Loading your progress",
  "partner.checking": "Checking your payout setup",

  "partner.progress.intro": "",
  "partner.progress.ready":
    "Setup complete.",
  "partner.progress.remaining": "Finish the remaining steps in the Lura app.",
  "partner.progress.action": "Set up payout account",
  "partner.progress.action.busy": "Preparing Stripe…",
  "partner.progress.error": "We couldn’t load your onboarding progress.",

  "partner.earlierSteps":
    "Finish your tax documents and legal agreements in the Lura app.",

  "partner.refresh.expired": "",
  "partner.refresh.starting": "Opening payout setup…",
  "partner.refresh.returning": "Opening payout setup…",
  "partner.refresh.error": "We couldn’t open payout setup.",
  "partner.refresh.seeProgress": "See your progress",

  "partner.complete.error": "We couldn’t load your onboarding progress.",
  "partner.complete.ready.title": "Payout account connected",
  "partner.complete.ready.all":
    "Setup complete.",
  "partner.complete.ready.partial":
    "Finish the remaining steps in the Lura app.",
  "partner.complete.incomplete.title": "Finish your payout setup",
  "partner.complete.incomplete.body":
    "",
  "partner.complete.incomplete.action": "Continue payout setup",

  "partner.none": "No partner application found.",
  "partner.none.hint": "",

  "partner.denied":
    "We can’t load partner onboarding for this account.",
  "partner.start.error": "We couldn’t start payout setup.",
  "partner.start.denied.refresh":
    "We can’t reopen payout setup from this link. Sign in again to check your progress.",
  "partner.start.denied":
    "Payout setup isn’t available for this account yet. Finish the earlier steps in the Lura app.",

  "partner.signedOut": "Sign in to continue.",
  "partner.signedOut.hint": "You can {signIn}, or continue in the Lura app.",

  "dashboard.title": "Partner dashboard",
  "dashboard.signedOut": "{signIn} to see your partner account.",
  "dashboard.body": "View commissions and payouts in the Lura app.",
  "dashboard.hint":
    "Open the Lura app to see your commissions and payouts. To finish setting up your account, go to {onboarding}.",
  "dashboard.hint.link": "partner onboarding",

  "adminApplication.title": "Partner application",
  "adminApplication.signedOut": "{signIn} to continue.",
  "adminApplication.body": "Application review isn’t available here.",
  "adminApplication.hint": "Application {id}. Reviews are handled through the admin tooling.",
} as const;

export type PartnerKey = keyof typeof en;

export const es: Partial<Record<PartnerKey, string>> = {
  // These paragraphs are intentionally omitted in both locales; no translated prose.
  "partner.progress.intro": "",
  "partner.refresh.expired": "",
  "partner.complete.incomplete.body": "",
  "partner.none.hint": "",
  "partner.title": "Configuración de socio",
  "partner.step.tax": "Documentos fiscales",
  "partner.step.legal": "Acuerdos legales",
  "partner.step.payout": "Cuenta para recibir pagos",
  "partner.step.where.app": "en la app de Lura",
  "partner.step.where.either": "aquí o en la app",
  "partner.step.line": "{label}: {state}",
  "partner.progress.remaining": "Termina los pasos pendientes en la app de Lura.",
  "partner.progress.action": "Configurar cuenta para recibir pagos",
  "partner.progress.action.busy": "Preparando Stripe…",
  "partner.complete.ready.title": "Cuenta para recibir pagos conectada",
  "partner.complete.incomplete.action": "Continuar configuración para recibir pagos",
  "partner.signedOut.hint": "Puedes {signIn} o continuar en la app de Lura.",
  "dashboard.title": "Panel de socio",
  "dashboard.signedOut": "{signIn} para ver tu cuenta de socio.",
  "dashboard.hint": "Abre la app de Lura para ver tus comisiones y los pagos que recibes. Para terminar de configurar tu cuenta, ve a {onboarding}.",
  "dashboard.hint.link": "configuración de socio",
  "adminApplication.title": "Solicitud de socio",
  "adminApplication.signedOut": "{signIn} para continuar.",
  "adminApplication.hint":
    "Solicitud {id}. Las revisiones se realizan mediante las herramientas de administración.",
  "adminApplication.body": "La revisión de solicitudes no está disponible aquí.",
  "dashboard.body": "Consulta tus comisiones y los pagos que recibes en la app de Lura.",
  "partner.checking": "Verificando tu configuración para recibir pagos",
  "partner.complete.error": "No pudimos cargar tu progreso en la configuración.",
  "partner.complete.incomplete.title": "Termina de configurar cómo recibirás tus pagos",
  "partner.complete.ready.all": "Configuración completa.",
  "partner.complete.ready.partial": "Termina los pasos pendientes en la app de Lura.",
  "partner.denied": "No podemos cargar la configuración de socio para esta cuenta.",
  "partner.earlierSteps": "Completa tus documentos fiscales y acuerdos legales en la app de Lura.",
  "partner.loading": "Cargando tu progreso",
  "partner.none": "No se encontró ninguna solicitud para el programa de socios.",
  "partner.progress.error": "No pudimos cargar tu progreso en la configuración.",
  "partner.progress.ready": "Configuración completa.",
  "partner.refresh.error": "No pudimos abrir la configuración para recibir pagos.",
  "partner.refresh.returning": "Abriendo la configuración para recibir pagos…",
  "partner.refresh.seeProgress": "Ver tu progreso",
  "partner.refresh.starting": "Abriendo la configuración para recibir pagos…",
  "partner.signedOut": "Inicia sesión para continuar.",
  "partner.start.denied": "La configuración para recibir pagos aún no está disponible para esta cuenta. Completa los pasos anteriores en la app de Lura.",
  "partner.start.denied.refresh": "No podemos volver a abrir la configuración para recibir pagos desde este enlace. Inicia sesión de nuevo para revisar tu progreso.",
  "partner.start.error": "No pudimos iniciar la configuración para recibir pagos.",
  "partner.step.done": "Listo",
  "partner.step.todo": "Sin terminar",
};
